import { action } from '@/lib/api.js';
import { updateAiSuggestion } from '@/redux/actions/gravityActions.js';
import { actionArgumentListSuggestions } from '@/redux/selectors/aiSelectors.js';
import DataTypeIcon from '@/screens/apps/components/DataTypeIcon';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import * as Popover from '@radix-ui/react-popover';
import { randomId } from '@shared/utils/randomId.js';
import { toTitleCase } from '@shared/utils/toTitleCase.js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AiSuggestionsContent from './AiSuggestionsContent.jsx';
import formatArgumentKey from '@/lib/formatArgumentKey.js';

const AiArgumentsSuggestionsPopover = ({ children, side = 'bottom', align = 'middle', location }) => {
  const actionId = useSelector((state) => appScreenSelectors.activeAppAction(state)?.id);
  const docUrl = useSelector((state) => appScreenSelectors.activeAppAction(state)?.documentation?.url);
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const suggestions = useSelector(actionArgumentListSuggestions) || [];

  const handleAddSelectedArguments = async (items) => {
    const key = `actions[id:${actionId}].arguments`;
    for (const suggestion of items) {
      const payload = {
        id: randomId(),
        location: suggestion?.location || 'body',
        key: formatArgumentKey(suggestion?.key, { isArray: suggestion?.dataType === 'array' }),
        externalKey: formatArgumentKey(suggestion?.externalKey, { isArray: suggestion?.dataType === 'array' }),
        required: false,
        name: suggestion?.label,
        dataType: suggestion?.dataType || 'string',
        hidden: false
      };
      await appActions.merge(key, 'id', payload);
      removeFromSuggestion(suggestion);
    }
    setOpen(false);
  };

  const removeFromSuggestion = (suggestion) => {
    setLocalSuggestions(localSuggestions.filter((s) => s.key !== suggestion.key));
    action('REMOVE_FROM_ARGUMENT_LIST_SUGGESTIONS', { key: suggestion.key, actionId });
  };

  const handleStopArgument = (arg) => {
    const updatedMeta = { ...arg?.meta, dismissedKeys: Array.from(new Set([...(arg?.meta?.dismissedKeys || []), arg?.key])) };
    updateAiSuggestion({ id: arg?.suggestionId, path: arg?.suggestionPath, action: 'updateMeta', meta: updatedMeta });
    action('UPDATE_AI_SUGGESTION_META', { suggestionId: arg?.suggestionId, meta: updatedMeta });
  };

  const handleStopAll = () => {
    const suggestion = localSuggestions?.[0];
    updateAiSuggestion({ id: suggestion?.suggestionId, path: suggestion?.suggestionPath, action: 'stop' });
    action('REMOVE_AI_SUGGESTION', { id: suggestion?.suggestionId });
  };

  const mappedSuggestions = useMemo(
    () =>
      localSuggestions.map((item) => ({
        ...item,
        title: item?.label,
        tag: toTitleCase(item?.location),
        icon: <DataTypeIcon label={item?.dataType} />
      })),
    [localSuggestions]
  );

  useEffect(() => {
    const filteredSuggestions = location ? suggestions.filter((suggestion) => suggestion.location === location) : suggestions;
    setLocalSuggestions(filteredSuggestions);
  }, [suggestions, location]);

  useEffect(() => {
    if (!open) {
      setLocalSuggestions([]);
    } else {
      const filteredSuggestions = location ? suggestions.filter((suggestion) => suggestion.location === location) : suggestions;
      setLocalSuggestions(filteredSuggestions);
    }
  }, [open]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <AiSuggestionsContent //
        open={open}
        type={'arguments'}
        title={'Add Arguments'}
        subtitle={`We've found ${suggestions.length} arguments from this action's docs.`}
        side={side}
        align={align}
        location={location}
        multiSelect={true}
        items={[{ title: 'Arguments', items: mappedSuggestions }]}
        onStopAll={handleStopAll}
        onStop={handleStopArgument}
        onMultiAccept={handleAddSelectedArguments}
        headerDocs={docUrl}
      />
    </Popover.Root>
  );
};

export default AiArgumentsSuggestionsPopover;
